import React, { useState, useEffect, useRef } from 'react';
import { IoVolumeMute, IoVolumeHigh } from 'react-icons/io5';
import ProjectCard from '../pages/projectcard';
import '../style/card.css';

import img1 from '../../images/asm.webp';
import img2 from '../../images/enp.webp';
import img3 from '../../images/isolve.webp';
import img4 from '../../images/fxm.jpg';

import vid1 from '../../assets/reels/events/Sahulat Markaz Final.mp4';
import vid2 from '../../assets/reels/industry/No Logo And Text.mp4';
import vid3 from '../../assets/reels/events/Rcm 2.mp4';
import vid4 from '../../assets/reels/vfx & hyperlaspe/Multsn X Fatima 2.mp4';

const projectsData = [
  { id: 1, title: 'Sahulat Markaz Launch Event, Shalimar', thumbnail: img1, video: vid1 },
  { id: 2, title: 'Expert Enterprise Video', thumbnail: img2, video: vid2 },
  { id: 3, title: 'iSolve RCM Annual Dinner 2024', thumbnail: img3, video: vid3 },
  { id: 4, title: 'Fatima Fertilizer Multan Sultans', thumbnail: img4, video: vid4 },
];

const ProjectsShowcase = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isMuted, setIsMuted] = useState(true);           // always start muted
  const [userWantsSound, setUserWantsSound] = useState(false); // becomes true after first unmute click

  const videoRef = useRef(null);

  const totalProjects = projectsData.length;

  // ───────────────────────────────────────────────
  // When slide changes → reset video to muted & try autoplay
  // ───────────────────────────────────────────────
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Always force muted on slide change (safe autoplay)
    video.muted = true;
    setIsMuted(true);

    video.currentTime = 0; // restart from beginning

    const playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => {
        // Autoplay failed - common & expected on mobile
      });
    }
  }, [currentIndex]);

  // ───────────────────────────────────────────────
  // Apply user preference AFTER slide change
  // ───────────────────────────────────────────────
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !userWantsSound) return;

    video.muted = isMuted;

    if (!isMuted) {
      // User wants sound → try to enable it
      video.play().catch(() => {
        console.log("Cannot play with sound yet - waiting for interaction");
        setIsMuted(true); // fallback
      });
    }
  }, [isMuted, userWantsSound]);

  const toggleMute = () => {
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    setUserWantsSound(true); // user has interacted → allow sound attempts

    const video = videoRef.current;
    if (video) {
      video.muted = newMuted;

      if (!newMuted) {
        // Try to play with sound now that user clicked
        video.play().catch((err) => {
          console.log("Play with sound blocked:", err);
          setIsMuted(true); // fallback to muted
        });
      }
    }
  };

  const nextProject = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => (prev + 1) % totalProjects);
    setTimeout(() => setIsTransitioning(false), 500);
  };

  const prevProject = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => (prev - 1 + totalProjects) % totalProjects);
    setTimeout(() => setIsTransitioning(false), 500);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') prevProject();
      if (e.key === 'ArrowRight') nextProject();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isTransitioning]);

  return (
    <div className="projects-section">
      <div className="projects-container">
        <div className="projects-header">
          <h2 className="projects-title">Featured Projects</h2>
          <p className="projects-subtitle">Showcasing our best work</p>
        </div>

        <div className="projects-carousel">
          <button
            onClick={prevProject}
            className="projects-nav-btn"
            aria-label="Previous project"
            disabled={isTransitioning}
          >
            ‹
          </button>

          <div style={{ position: 'relative', width: '100%', maxWidth: '1000px', margin: '0 auto' }}>
            <div className={`projects-wrapper ${isTransitioning ? 'transitioning' : ''}`}>
              <ProjectCard
                project={projectsData[currentIndex]}
                isActive={true}
                videoRef={videoRef}
                isMuted={isMuted}
              />
            </div>

            <button
              onClick={toggleMute}
              className="volume-control-btn"
              aria-label={isMuted ? 'Unmute video' : 'Mute video'}
              title={isMuted ? 'Click to enable sound' : 'Click to mute'}
            >
              {isMuted ? <IoVolumeMute size={26} /> : <IoVolumeHigh size={26} />}
            </button>
          </div>

          <button
            onClick={nextProject}
            className="projects-nav-btn"
            aria-label="Next project"
            disabled={isTransitioning}
          >
            ›
          </button>
        </div>

        <div className="projects-dots">
          {projectsData.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                if (!isTransitioning) {
                  setIsTransitioning(true);
                  setCurrentIndex(index);
                  setTimeout(() => setIsTransitioning(false), 500);
                }
              }}
              className={`projects-dot ${index === currentIndex ? 'active' : ''}`}
              aria-label={`Go to project ${index + 1}`}
            />
          ))}
        </div>

        <div className="projects-counter">
          {currentIndex + 1} / {totalProjects}
        </div>
      </div>
    </div>
  );
};

export default ProjectsShowcase;